<template>
  <div class="equity">
    <div class="equity-container">
      <div
        v-if="hasActionPermission('center_equity_view_equity')"
        class="flex justify-between"
      >
        <a-space :size="10">
          <span class="label-h1">{{ $t('dang-qian-quan-yi') }}</span>
          <XLevelTag class="h-[22px] !min-w-[76px]" />
          <template v-if="!isTrial">
            <span
              v-if="userInfo?.is_expired"
              class="text-[12px] leading-[18px] text-font-50"
            >
              {{ $t('yi-guo-qi') }}
            </span>
            <span v-else class="text-[12px] text-font-40">
              {{ $t('dao-qi-shi-jian') }}：{{ currentExpireTime }}
            </span>
          </template>
          <span v-if="isSubscribed" class="subscription-tag">
            {{ $t('lian-xu-bao-yue-sheng-xiao-zhong') }}
          </span>
        </a-space>
        <a-dropdown
          v-if="isSubscribed && isGlobalVersion()"
          :trigger="['click']"
        >
          <a-button class="px-[9px]" size="middle">...</a-button>
          <template #overlay>
            <a-menu>
              <a-menu-item key="0" @click="onCancelSubscribe">
                {{ $t('qu-xiao-ding-yue') }}
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
        <!-- <a-button v-if="showPackageBuy" size="small" type="primary" @click="handleBuy">续费</a-button> -->
      </div>
      <a-spin :spinning="loading">
        <BuyTip
          v-if="
            !showPackageBuy && hasActionPermission('center_equity_view_equity')
          "
          @contact="onContactUs"
        />
        <div
          v-if="hasActionPermission('center_equity_view_equity')"
          class="lg:flex lg:gap-[20px] pt-[20px]"
        >
          <div class="flex gap-[20px] w-full">
            <FeatureCoin
              v-if="!isTrialAccountWithoutCreditCoin"
              class="!w-2/5"
              :disabled="userInfo?.is_expired"
            >
              <template #extra>
                <a class="cursor-pointer" @click="showDetailModal">
                  {{ $t('cha-kan-xiang-qing') }}
                </a>
                <ExtraBtn
                  v-if="
                    canBuyCredit &&
                    hasActionPermission('center_equity_buy_yanbi')
                  "
                  size="large"
                  class="ml-[16px]"
                  :text="$t('jia-gou')"
                  :disabled="userInfo?.is_expired"
                  @click="onAddCreditCoins"
                >
                  <template #icon>
                    <iconpark-icon name="yanbi"></iconpark-icon>
                  </template>
                </ExtraBtn>
              </template>
            </FeatureCoin>
            <FeatureStorage class="!w-3/5" @upgrade="handleBuy">
              <template #extra>
                <StorageModal class="cursor-pointer" />
                <ExtraBtn
                  v-if="
                    canBuyStorage &&
                    hasActionPermission('center_equity_buy_storage')
                  "
                  size="large"
                  :text="$t('jia-gou')"
                  class="ml-[16px]"
                  :disabled="userInfo?.is_expired"
                  @click="addStorage"
                >
                </ExtraBtn>
              </template>
            </FeatureStorage>
            <div
              v-if="isTrialAccountWithoutCreditCoin"
              class="flex flex-col h-auto !w-2/5"
            >
              <div class="w-full h-full">
                <Feature
                  :feature-type="RIGHTS_FEATURE.sub_account_num"
                  :feature-state="featureInfo.sub_account_num"
                  :usage="subAccountUsage"
                  :disabled="userInfo?.is_expired"
                />
              </div>
              <div class="w-full h-full">
                <Feature
                  class="w-full"
                  :feature-type="RIGHTS_FEATURE.rendering_channel_num"
                  :feature-state="featureInfo.rendering_channel_num"
                  :rendering_channel_num="currentRights?.rendering_channel_num"
                  :disabled="userInfo?.is_expired"
                />
              </div>
            </div>
          </div>
          <div
            v-if="!isTrialAccountWithoutCreditCoin"
            class="data-list h-auto w-full"
          >
            <div class="w-2/5 lg:w-full h-full">
              <Feature
                :feature-type="RIGHTS_FEATURE.sub_account_num"
                :feature-state="featureInfo.sub_account_num"
                :usage="subAccountUsage"
                :disabled="userInfo?.is_expired"
              />
            </div>
            <div class="w-3/5 lg:w-full h-full">
              <Feature
                class="!w-2/3 lg:!w-full"
                :feature-type="RIGHTS_FEATURE.rendering_channel_num"
                :feature-state="featureInfo.rendering_channel_num"
                :rendering_channel_num="currentRights?.rendering_channel_num"
                :disabled="userInfo?.is_expired"
              />
            </div>
          </div>
        </div>
        <div
          v-if="hasActionPermission('center_equity_expand_equity')"
          class="mt-[30px]"
        >
          <div class="flex justify-between">
            <span class="label-h1">{{ $t('kuo-zhan-quan-yi') }}</span>
            <div class="flex-y-center gap-[10px]">
              <a-button
                v-if="checkPermission(ActionListKey.REDEMPTION_CODE)"
                type="link"
                class="flex-center"
                @click="handVoucher"
              >
                <iconpark-icon
                  name="duihuan"
                  size="20"
                  class="mr-[4px]"
                ></iconpark-icon>
                {{ $t('dui-huan-ma-dui-huan') }}
              </a-button>
              <UpgradeBtn
                v-if="showPackageBuy"
                :is-trial="isTrial"
                @click="handleBuy"
              />
            </div>
          </div>
          <RightsSame
            v-if="!pending"
            class="mt-[20px]"
            :accountList="accountList?.data || []"
            :rightsList="compareRightsHeader"
            :compareRights="compareRights"
            :colorsMap="colorsMap"
            :showTitle="false"
          ></RightsSame>
        </div>
      </a-spin>
    </div>

    <CoinListModal
      ref="creditCoin"
      v-model:only-valid="onlyValid"
      class="w-[63%]"
      @chart-data="setChartData"
    />

    <UnsubscribeModal v-model:open="unsubscribeModalOpen" />
  </div>
</template>
<script lang="ts" setup name="Equity">
import { useRightsInfo } from './useRightsInfo'
import BuyTip from './components/buy-tip.vue'
import ExtraBtn from './components/extra-btn.vue'
import UpgradeBtn from './components/upgrade-btn.vue'
import FeatureCoin from './components/featureCoin.vue'
import Feature from './components/feature.vue'
import CoinListModal from './components/coinDetailModal.vue'
import FeatureStorage from './components/featureStorage.vue'
import StorageModal from './components/storageModal.vue'
import UnsubscribeModal from './components/unsubscribe-modal.vue'
import RightsSame from '~/pages/subscribe/components/rights-same/index.vue'
import { useRequestAuth } from '~/hooks/useRequestAuth'
import { useVoucher } from '~/hooks/useModal/modals/voucher'

import {
  useAddFeature,
  useCustomerService,
  usePaymentEquity,
  useAddStorage,
} from '@/features/paymentDesk'
import {
  RIGHTS_FEATURE,
  type IEquity,
  type IChartData,
  ORDER_TYPE,
  ORDER_BEHAVIOR,
  type IRightsCompare,
} from '~/api/interface/rights'
import { reqCreditCoinOrder, reqRightsDisplay } from '~/api/module/rights'
import { SHOW_ORG_KEY, USER_LEVEL_KEY } from '~/api/interface'
import { usePermission } from '~/hooks/usePermission.ts'
import { getUserAccountList } from '~/api/module/auth'

import {
  useRightsConfig,
  actionSetupMySubscriptions,
} from '~/composables/rights'

import { EDITOR_PAY_TYPE } from '~/constants/rights'
import { useTime } from '~/hooks/useTime'
import { PageKey } from '~/constants/page'

const { canBuyStorage, canBuyCredit } = usePermission()

const { hasActionPermission } = usePageAndActionPermissionData()

const {
  userRightsType,
  isTrial,
  isTrialAccount,
  showPackageBuy,
  buyRightsType,
} = useRightsInfo()

const unsubscribeModalOpen = ref(false)
const route = useRoute()

const rightsConfig = useRightsConfig()

const isSubscribed = computed(() => {
  return rightsConfig.value.subscription?.id
})

actionSetupMySubscriptions()

const userInfo = useUserInfo()
const { data: accountList, pending } = useAsyncData(() => {
  if (userInfo.value) {
    return getUserAccountList()
  }

  return Promise.resolve({
    code: 0,
    data: [],
  })
})

const creditCoin = ref()
const onlyValid = ref(true) // 只看有效言币记录
const rights = ref<IEquity[]>([])
const rightsCompare = ref<IRightsCompare[]>([])
const loading = ref(false)
const colorsMap = {
  0: 'var(--back-bg)',
  [USER_LEVEL_KEY.TASTE]: 'var(--subscribe-table-taste)',
  [USER_LEVEL_KEY.PERSONAL]: 'var(--subscribe-table-personal)',
  [USER_LEVEL_KEY.PERSONAL_LITE]: 'var(--subscribe-table-lite)',
  [USER_LEVEL_KEY.TEAM_LIGHT]: 'var(--subscribe-table-light)',
  [SHOW_ORG_KEY.ORG_SHOW]: 'var( --subscribe-table-org)',
}

const onCancelSubscribe = () => {
  unsubscribeModalOpen.value = true
}

const featureInfo = reactive({
  credit_coin: {
    visible: true, // 用不到
    addable: false,
  },
  sub_account_num: {
    visible: false,
    addable: false,
  },
  rendering_channel_num: {
    visible: false,
    addable: false,
  },
  // storage: {
  //   visible: false,
  //   addable: false,
  // },
})
const isTrialAccountWithoutCreditCoin = ref(true) // 试用版且无试用言币，默认“是”
const chartData = ref<IChartData>() // 图表数据

const currentRights = computed(() => userInfo.value?.rights) // 当前登录用户的权益数值
const subAccountUsage = computed(() => {
  const stat = {
    remain: 0,
    total: 0,
    percent: 0,
  }
  if (currentRights.value) {
    stat.remain = currentRights.value.sub_account_num
    stat.total = currentRights.value.total_sub_account_num
    stat.percent = (stat.remain / stat.total) * 100
  }
  return stat
})

// 权益对比头部信息
const compareRightsHeader = computed(() => {
  return rights.value?.filter((r) => {
    const isRightsTypeOk = [
      USER_LEVEL_KEY.TASTE,
      USER_LEVEL_KEY.PERSONAL,
      USER_LEVEL_KEY.PERSONAL_LITE,
      USER_LEVEL_KEY.TEAM_LIGHT,
      SHOW_ORG_KEY.ORG_SHOW,
    ].includes(r.rights_type)

    return isRightsTypeOk
  })
})

const compareRights = computed(() => {
  return rightsCompare.value
})

const { $t } = useNuxtApp()

// 加购存储空间
const addStorage = () => {
  useAddStorage({
    title: $t('jia-gou-cun-chu-kong-jian'),
    user_info: userInfo.value,
    behavior: ORDER_BEHAVIOR.ADD_PURCHASE,
    feature_type: RIGHTS_FEATURE.storage,
    order_type: ORDER_TYPE.STORAGE,
  })
}

const { timeFormat } = useTime()

const currentExpireTime = computed(() =>
  timeFormat(userInfo.value?.current_package_expired_time, 'YYYY/MM/DD')
)

function setChartData(data: IChartData) {
  chartData.value = data
}

// 查看言币详情弹窗
const showDetailModal = () => {
  creditCoin.value.toggleShow()
}

function handVoucher() {
  useVoucher(() => {
    creditCoin.value?.reload?.()
  })
}

function onAddCreditCoins() {
  if (userInfo.value?.is_expired) {
    return
  }

  onAddFeature({
    title: $t('jia-gou-yan-bi'),
    feature_type: RIGHTS_FEATURE.credit_coin,
    order_type: ORDER_TYPE.CREDITS,
  })
}

function onContactUs() {
  useCustomerService()
}
function onAddFeature(options: {
  title: string
  feature_type: RIGHTS_FEATURE
  order_type: ORDER_TYPE
}) {
  if (!userInfo.value) {
    return
  }

  useAddFeature({
    ...options,
    user_info: userInfo.value,
    behavior: ORDER_BEHAVIOR.ADD_PURCHASE,
  })
}

// 续费权益
function handleBuy() {
  if (isTrial?.value) {
    trackEvent(TRACK_EVENT.POPUP_PAY_PAGE_CLICK, {
      source: SOURCE.RIGHT_MANAGE,
    })
  } else {
    trackEvent(TRACK_EVENT.POPUP_PAY_PAGE_CLICK, {
      source: SOURCE.RIGHT_CONTINUE,
    })
  }

  const $r = rights.value?.find((r) => r.rights_type === buyRightsType.value)
  if (!$r || !userInfo.value) {
    return
  }

  if (isGlobalVersion()) {
    navigateTo({ name: PageKey.SUBSCRIBE })
  } else {
    usePaymentEquity({
      title: $r?.rights_name,
      isRenew: !isTrial?.value,
      userInfo: userInfo?.value,
      rightsType: $r?.rights_type,
      behavior: isTrial.value ? ORDER_BEHAVIOR.BUY : ORDER_BEHAVIOR.RENEW,
      order_type: ORDER_TYPE.PACKAGE,
    })
  }
}

function getRights() {
  useRequestAuth(() => reqRightsDisplay())
    .then(({ code, data }) => {
      if (!code && data) {
        rights.value = data.rights
        rightsCompare.value = data.rights_compare
        if (route.query?.pay_type) {
          checkEditor()
        }
      }
    })
    .catch((error) => {
      consola.error(error)
    })
}

async function checkTrialAccount() {
  const { code, data } = await reqCreditCoinOrder({ check_all: '1' })
  if (code || !data) {
    return
  }

  const records = data.order_list
  isTrialAccountWithoutCreditCoin.value =
    records.length === 1 && isTrialAccount(records[0].rights_type)
}

function updateFeatureInfo() {
  if (!userRightsType.value) {
    return false
  }

  const isOrgStandard = [
    USER_LEVEL_KEY.ORG_STANDARD,
    USER_LEVEL_KEY.TEAM_LIGHT,
    USER_LEVEL_KEY.TEAM_TASTE,
  ].includes(userRightsType.value)

  const isOrgFlagship = userRightsType.value === USER_LEVEL_KEY.ORG_FLAGSHIP

  featureInfo.sub_account_num.visible = isOrgStandard || isOrgFlagship
  featureInfo.sub_account_num.addable = false // 企业才会显示，但是无法操作
  featureInfo.rendering_channel_num.visible = isOrgFlagship
  featureInfo.rendering_channel_num.addable = false // 企业才会显示，但是无法操作
}

function checkEditor() {
  const userInfo = useUserInfo()
  const cur = route.query?.pay_type
  // 购买言币
  if (
    cur === EDITOR_PAY_TYPE.COIN &&
    [
      USER_LEVEL_KEY.PERSONAL,
      USER_LEVEL_KEY.TEAM_LIGHT,
      USER_LEVEL_KEY.PERSONAL_LITE,
    ].includes(Number(userInfo.value?.rights_type))
  ) {
    onAddCreditCoins()
  }

  // 体验版升级权益
  if (
    cur === EDITOR_PAY_TYPE.RIGHTS &&
    [USER_LEVEL_KEY.TASTE, USER_LEVEL_KEY.TEAM_TASTE].includes(
      Number(userInfo.value?.rights_type)
    )
  ) {
    nextTick(() => {
      handleBuy()
    })
  }
}

updateFeatureInfo()

watch(() => userInfo.value?.user_id, updateFeatureInfo) // 切换账号

onBeforeMount(async () => {
  try {
    trackEvent(TRACK_EVENT.PAGE_EQUITY_MANAGE, {
      team_id: userInfo.value?.organization_id,
      team_name: userInfo.value?.organization_name,
      operator_id: userInfo.value?.user_id,
      operator_name: userInfo.value?.account_name,
    })

    loading.value = true
    await useUpdateUserAndAccounts().then(getRights)
    await checkTrialAccount()
    loading.value = false
  } catch (e) {
    console.error(e)
    loading.value = false
  }
})
</script>

<style scoped lang="scss">
.subscription-tag {
  background: var(--purple-1);
  border-radius: 5px;
  line-height: 18px;
  font-size: 12px;
  padding: 2px 6px;
  color: var(--text-90);
  // color: rgba(18, 25, 38, 0.9);
}
</style>

<style lang="scss">
@use './index.scss';
</style>
